// PATH: src/components/catalog/useCatalogData.ts
import * as React from "react";
import { useCurriculumApi } from "../../api";
import type { CurriculumResponse } from "../../models/domain";
import { errorMessage } from "../ui";
import { indexCatalog, type CatalogIndex } from "./catalogModel";

/**
 * Le catalogue entier en UN appel (`GET /v1/curriculum/overview`), relu après
 * chaque écriture. `refreshing` : relecture en cours, l'écran garde l'ancien
 * état affiché (pas d'écran blanc).
 */
export const useCatalogData = () => {
  const { getCurriculumOverview } = useCurriculumApi();
  const fetchRef = React.useRef(getCurriculumOverview);
  fetchRef.current = getCurriculumOverview;

  const [data, setData] = React.useState<CurriculumResponse | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [refreshing, setRefreshing] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const load = React.useCallback(async (silent = false) => {
    if (silent) setRefreshing(true);
    else setLoading(true);
    setError(null);
    try {
      const { data: resp } = await fetchRef.current();
      setData({ programs: resp?.programs ?? [] });
    } catch (e) {
      setError(errorMessage(e, "Impossible de charger le catalogue."));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  React.useEffect(() => {
    void load();
  }, [load]);

  const index: CatalogIndex = React.useMemo(() => indexCatalog(data), [data]);
  const reload = React.useCallback(() => load(true), [load]);

  return { data, index, loading, refreshing, error, reload, retry: load };
};
